import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { applicationState } from 'shared/store/applicationState'
import { FatigueAssessmentForm, DashboardQueryModel, FatigueAssessmentTaskEnum, FatigueAssessment, LocationDetail } from 'shared/models/api/models';
import { getFatigueAssessments, getDashboardFatigueAssessments } from 'shared/services/fatigueAssessmentApi';
import { getLocations, getLocationsWithDetail } from 'shared/services/locationApi';
import { IUserSearchResult, Location, FatigueAssessmentRiskEnum } from 'shared/models/api/models';
import { toISOLocalDate } from "shared/models/mapping/mapping";
import { Toggle } from "components/ui/ToggleContent";
import { PageList } from "components/ui/PageList";
import { Chip } from "@mui/material";
import { FilterArrow } from 'components/portal/GridComponents';
import { ToggleButton } from 'components/ui/ToggleButton';
import Card from './FatigueAssessmentCard';
import { FatigueAssessmentView } from './FatigueAssessmentView';
import { UserChoice } from './FormControls';

import {
    Input,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu
} from 'reactstrap';

import {
    myForm,
    shiftSummaryDashboardPending,
    shiftSummaryDashboardToAction,
    shiftSummaryDashboardToRevisit
} from "./FatigueAssessmentPageSelector";

import {
    fatigueAssessmentRoute,
    fatigueAssessmentPendingPathRoute,
    fatigueAssessmentActionPathRoute,
    fatigueAssessmentRevisitPathRoute,
    fatigueAssessmentCompletedPathRoute
} from 'components/portal/fatigueAssessment/FatigueAssessmentRoutes';

import {
    ChevronRight as LinkIcon,
    ArrowDownward as SortDescIcon,
    ArrowUpward as SortAscIcon,
    ImportExport as SortNoneIcon,
    NearMeOutlined,
    SupervisorAccountOutlined
} from '@mui/icons-material';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from 'recharts';


enum OrderByOption {
    NameAsc,
    NameDesc,
    ShiftStartTimeAsc,
    ShiftStartTimeDesc
}

class FilterType {
    static supervisor: string = 'Supervisor';
    static location: string = 'Location';
    static date: string = 'Date';
}


interface TitleProps {
    title: string,
    titleIcon?: any
}

const TitleContainer = (props: TitleProps) => {
    return (
        <div className="fa-list-title">
            {props.titleIcon}
            <div className="fa-list-title-text">{props.title}</div>
        </div>
    );
}


interface IPageSizeDropdownProps {
    pageSize: number,
    setPageSize: (pageSize: number) => void,
    defaultExpanded?: boolean,
    sizes: number[]
}

const PageSizeDropdown = (props: IPageSizeDropdownProps) => {
    return (
        <span className="psl-list-container">
            <div className="psl-list">
                {props.sizes.map(s =>
                    <div key={s}
                        className={`psl-item${ s === props.pageSize ? " selected" : "" }`}
                        onClick={() => props.setPageSize(s)}>{s} items</div>
                )}
            </div>
        </span>
    );
}

const PageSizeWithPicker = (props: IPageSizeDropdownProps) => {
    return (
        <Toggle label={props.pageSize + " items"} collapseOnChangeOf={props.pageSize}>
            <PageSizeDropdown {...props} />
        </Toggle>
    );
};


interface ResultCountProps {
    viewCount: number,
    resultCount: number,
    pageSize: number,
    setPageSize: any,
    link?: string,
    visible?: boolean
}

const ResultCountContainer = (props: ResultCountProps) => {

    if (!props.visible) {
        return (<></>);
    }

    return (
        <div className="fa-list-resultcount float-right">
            {props.link &&
                <Link to={props.link} className="fa-list-resultcount-link">
                    <span className="fa-list-resultcount-text">{props.viewCount} of {props.resultCount} results</span>
                    <LinkIcon />
                </Link>
            }
            {!props.link &&
                <div className="fa-list-resultcount-itemsPerPage">
                    <span className="picker">
                        <PageSizeWithPicker
                            pageSize={props.pageSize}
                            setPageSize={props.setPageSize}
                            sizes={[10, 25, 50, 100]} />
                    </span>
                    <span> per page of {props.resultCount} results</span>
                </div>
            }
        </div>
    );
}


interface TabsProps {
    tabs: FatigueAssessmentView[],
    currentView: FatigueAssessmentView,
    setCurrentView: any,
    visible?: boolean
}

const TabsContainer = (props: TabsProps) => {

    const getTabLabel = (view: FatigueAssessmentView) => {
        let selectedPage = myForm;

        if (view == FatigueAssessmentView.Pending) {
            selectedPage = shiftSummaryDashboardPending;
        }
        else if (view == FatigueAssessmentView.ToAction) {
            selectedPage = shiftSummaryDashboardToAction;
        }
        else if (view == FatigueAssessmentView.ToRevisit) {
            selectedPage = shiftSummaryDashboardToRevisit;
        }

        let title = selectedPage.label;
        if (title.startsWith("\u2003")) {
            title = title.substr(title.indexOf(" ") + 1);
        }

        return title;
    }

    useEffect(() => {
    }, [props.currentView]);

    if (!props.visible) {
        return (<></>);
    }

    return (
        <div className="fa-list-tabs">
            {props.tabs.map((view) =>
                <div key={view} className={`fa-tab${ view === props.currentView ? " fa-tab-selected" : "" }`} onClick={() => props.setCurrentView(view)}>
                    {getTabLabel(view)}
                </div>
            )}
        </div>
    );
}
interface IChartContainerProps {
    assessments: FatigueAssessmentForm[],
    currentView: FatigueAssessmentView
}

const ChartContainer = (props: IChartContainerProps) => {
    const COLORS = {
        "Completed": "#01ab8b",
        "High Risk": "#ff0000",
        "Medium Risk": "#fab636",
        "Incomplete": "#234483"
    } as { [key: string]: string };


    const data = props.assessments.reduce((acc, form) => {

        let riskLevel = "Completed";
        if (form.risk == FatigueAssessmentRiskEnum.High) {
            riskLevel = "High Risk";
        }
        if (form.risk == FatigueAssessmentRiskEnum.Medium) {
            riskLevel = "Medium Risk";
        }
        if (!form.risk && !form.assessmentDate) {
            riskLevel = "Incomplete";
        }
        const existing = acc.find(item => item.name === riskLevel);
        if (existing) {
            existing.value++;
        } else {
            acc.push({ name: riskLevel, value: 1 });
        }
        return acc;
    }, [] as { name: string, value: number }[]);

    if (props.currentView === FatigueAssessmentView.Overview && data.length > 0)
        return (
            <>
                {/* <ResponsiveContainer width="400" height="400"> */}
                <PieChart width={400} height={400}>
                    <Legend layout="horizontal" verticalAlign="top" align="center" />
                    <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                        {data.map((entry, index) => (
                            <Cell key={`cell-${ index }`} fill={COLORS[entry.name]} />
                        ))}
                    </Pie>
                </PieChart>
                {/* </ResponsiveContainer > */}
            </>
        );
    else {
        return (<></>);
    }
}

interface FilterItem {
    type: string,
    label: string,
    value: any,
}

interface AnswerProps {
    onAnswerUpdate: (filter: FilterItem) => any;
}

const LocationChoice = (props: AnswerProps) => {
    const [locations, setLocations] = useState([] as LocationDetail[]);

    const onAnswerUpdate = (label: string, value: any) => {
        if (value !== undefined) {
            let filter = {
                type: FilterType.location,
                label: label,
                value: value
            } as FilterItem;
            props.onAnswerUpdate(filter);
        }
    }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getLocationsWithDetail();
            if (mounted) {
                setLocations(results);
            }
        };
        mount();
        return () => { mounted = false };
    }, []);

    return (
        <div className="fa-filter-input fa-filter-input-sm">
            <UncontrolledDropdown>
                <DropdownToggle tag="div" className="rbt-input-main form-control rbt-input">
                    <div className="fa-select-options">Select</div>
                </DropdownToggle>
                <DropdownMenu className="fa-filter-location">
                    {locations.map((location) => (
                        <div key={location.id} className="form-check-inline dropdown-item" onClick={() => onAnswerUpdate(location.name, location.id)}>
                            <Label className="form-check-label">{`${ location.name }${ location.siteMobileNumber ? ` (${ location.siteMobileNumber })` : '' }`}</Label>
                        </div>

                    ))}
                </DropdownMenu>
                <ToggleButton isOpen={true} />
                <ToggleButton className="toggle-closed" />
            </UncontrolledDropdown>
        </div>
    );
}

const DateChoice = (props: AnswerProps) => {
    const dateTimeFormat = new Intl.DateTimeFormat("en-AU", {});

    const updateDate = (event: any) => {
        if (event !== undefined) {
            if (Date.parse(event.target.value)) {
                let filter = {
                    type: FilterType.date,
                    label: dateTimeFormat.format(new Date(event.target.value)),
                    value: event.target.value
                } as FilterItem;
                props.onAnswerUpdate(filter);
            }
        }
    }

    return (
        <div className="fa-filter-input fa-filter-input-sm fa-filter-date">
            <Input type="date" id="date"
                onChange={updateDate}
                onClick={updateDate}
                defaultValue={toISOLocalDate(new Date())} />
        </div>
    );
}


interface FilterProps extends AnswerProps {
    onDelete: (index: number) => any,
    filters: FilterItem[],
    showSupervisor: boolean,
    visible?: boolean
}

const FilterContainer = (props: FilterProps) => {

    if (!props.visible) {
        return (<></>);
    }

    const onSupervisorUpdate = (userResult: IUserSearchResult) => {
        let supervisorFilter = {
            type: FilterType.supervisor,
            label: userResult.fullname,
            value: userResult.id
        } as FilterItem;

        props.onAnswerUpdate(supervisorFilter)
    }
    const title = props.showSupervisor ? "Filter by shift location, date or supervisor" : "Select shift location and date";
    return (
        <div className="fa-list-filter">
            <div className="fa-list-filter-title">{title}</div>
            <div className="fa-list-filter-body">

                {props.showSupervisor &&
                    <div className="row">
                        <div className="col-3 col-xl-2 fa-filter-label">Supervisor</div>
                        <div className="col">
                            <UserChoice
                                multichoice={true}
                                supervisorFilter={true}
                                onAnswerUpdate={onSupervisorUpdate} />
                        </div>
                    </div>
                }

                <div className="row">
                    <div className="col-3 col-xl-2 fa-filter-label">Shift Location</div>
                    <div className="col">
                        <LocationChoice onAnswerUpdate={props.onAnswerUpdate} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-3 col-xl-2 fa-filter-label">Date</div>
                    <div className="col">
                        <DateChoice onAnswerUpdate={props.onAnswerUpdate} />
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        {
                            props.filters.map((filter, i) => (
                                <Chip
                                    key={i}
                                    label={`${ filter.type } - ${ filter.label }`}
                                    onDelete={() => props.onDelete(i)}
                                />
                            ))
                        }
                    </div>
                </div>

            </div>
        </div>
    );
}


interface FaListProps {
    loading: boolean,
    adminView?: boolean,
    currentView: FatigueAssessmentView,
    assessments: FatigueAssessmentForm[],
    pageNumber: number,
    pageSize: number,
    orderByOption: OrderByOption,
    orderByName: any,
    orderByShiftStartTime: any
}

const FaListContainer = (props: FaListProps) => {
    const [displayAssessments, setDisplayAssessments] = useState(props.assessments.slice(0, props.pageSize));
    const [riskOptions, setRiskOptions] = useState([] as string[]);
    const [riskFilter, setRiskFilter] = useState([] as FatigueAssessmentRiskEnum[]);

    const nameOrderIcon = () => {
        if (props.orderByOption === OrderByOption.NameAsc) {
            return (<SortDescIcon />);
        }
        else if (props.orderByOption === OrderByOption.NameDesc) {
            return (<SortAscIcon />);
        }
        return (<SortNoneIcon />);
    }

    const shiftStartTimeOrderIcon = () => {
        if (props.orderByOption === OrderByOption.ShiftStartTimeAsc) {
            return (<SortDescIcon />);
        }
        else if (props.orderByOption === OrderByOption.ShiftStartTimeDesc) {
            return (<SortAscIcon />);
        }
        return (<SortNoneIcon />);
    }

    const filterRisk = ((risks: string[]) => {
        if (risks.length === 0 || risks[0] === 'All') {
            setRiskFilter([]);
            return;
        }

        let newRisks = [] as FatigueAssessmentRiskEnum[];

        risks.map(r => {
            let riskValue = Object.keys(FatigueAssessmentRiskEnum)[Object.values(FatigueAssessmentRiskEnum).indexOf(r)];
            newRisks.push(parseInt(riskValue));
        });

        setRiskFilter(newRisks);

    });

    useEffect(() => {
        let start = (props.pageNumber - 1) * props.pageSize;

        let newDisplayAssessments = props.assessments.slice(start, start + props.pageSize);

        if (riskFilter.length > 0) {
            newDisplayAssessments = newDisplayAssessments.filter((item) => riskFilter.includes(item.risk || 0))
        }

        setDisplayAssessments(newDisplayAssessments);

        setRiskOptions(
            props.assessments
                .filter((item, i, arr) => arr.findIndex(a => a.risk === item.risk) === i)
                .map(a => (a.risk || 0) as number)
                .sort()
                .map(r => FatigueAssessmentRiskEnum[r])
        );

    }, [props.assessments, props.pageNumber, props.pageSize, riskFilter]);

    return (
        <div className="fa-list-body">
            {props.loading === true ?
                <div className="p-4 text-center">Loading...</div>
                :
                <>
                    <div className="row assessment-card-row">

                        {props.adminView === true &&
                            <>
                                <div className="fa-col-status d-none d-xl-inline-block">Status</div>
                            </>
                        }
                        <div className="fa-col-due">Due</div>

                        <div className="col cursor-pointer" onClick={props.orderByName}>Name {nameOrderIcon()}</div>

                        <div className="fa-col-location d-none d-xl-inline-block">Location</div>

                        <div className="fa-col-shift-start-time d-none d-xl-inline-block cursor-pointer" onClick={props.orderByShiftStartTime}>Shift start time {shiftStartTimeOrderIcon()}</div>

                        {(props.currentView === FatigueAssessmentView.Overview || props.currentView === FatigueAssessmentView.ToAction) &&
                            <>
                                <div className="col d-none d-xl-inline-block">Supervisor</div>
                            </>
                        }

                        {props.adminView === true &&
                            <>
                                <div className="fa-col-risk d-none d-sm-inline-block">
                                    <FilterArrow text="Risk" filterItems={riskOptions} onFilter={filterRisk} />
                                </div>

                                <div className="fa-col-task d-none d-lg-inline-block">Task</div>

                                {props.currentView !== FatigueAssessmentView.Overview &&
                                    <div className="fa-col-action">Action</div>
                                }

                                <div className="fa-col-info">Info</div>
                            </>
                        }

                    </div>

                    <div className="fatigue-assessment-divider row"></div>

                    {(displayAssessments !== undefined && displayAssessments.length > 0) ? displayAssessments.map((assessment) => (
                        <Card key={assessment.id} assessment={assessment} adminView={props.adminView} currentView={props.currentView} />
                    ))
                        :
                        <div className="p-4 text-center">There are no actionable fatigue assessments to display.</div>
                    }
                </>
            }
        </div>
    );
}


interface FooterProps {
    pageNumber: number,
    pageSize: number,
    totalResults: number,
    moveToPage: any,
    visible?: boolean
}

const FooterContainer = (props: FooterProps) => {

    if (!props.visible) {
        return (<></>);
    }

    return (
        <div className="fa-list-footer">
            <div className="page-list">
                <PageList {...props} />
            </div>
        </div>
    );
}

interface FatigueAssessmentCardListProps {
    view: FatigueAssessmentView[],
    title: string,
    titleIcon?: any,
    showPageSize?: boolean,
    showHeaderLink?: boolean,
    showFilters?: boolean,
    showFooter?: boolean
}

const FatigueAssessmentCardList = (props: FatigueAssessmentCardListProps) => {
    const [currentView, setCurrentView] = useState(props.view[0]);
    const [assessmentCacheTime, setAssessmentCacheTime] = useState(new Date().getTime());
    const [assessments, setAssessments] = useState(undefined as FatigueAssessmentForm[] | undefined);
    const [viewAssessments, setViewAssessments] = useState([] as FatigueAssessmentForm[]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(25);
    const [pageNumber, setPageNumber] = useState(1);
    const [headerLink, setHeaderLink] = useState(undefined as string | undefined);
    const [adminView, setAdminView] = useState(false);
    const [orderByOption, setOrderByOption] = useState(OrderByOption.ShiftStartTimeAsc);
    const [loadBackground, setLoadBackground] = useState(false);

    const dateTimeFormat = new Intl.DateTimeFormat("en-AU", {});

    const getDefaultFilters = () => {
        let defaultFilters = [] as FilterItem[];
        let profile = applicationState.getProfile();
        // if (profile && currentView === FatigueAssessmentView.Overview) {
        //     defaultFilters.push(
        //         {
        //             type: FilterType.supervisor,
        //             label: `${ profile.firstname } ${ profile.lastname }`,
        //             value: profile.id
        //         } as FilterItem
        //     );
        // }
        const todayDate = new Date();
        let nowDate = toISOLocalDate(todayDate);
        defaultFilters.push(
            {
                type: FilterType.date,
                label: dateTimeFormat.format(todayDate),
                value: nowDate
            } as FilterItem
        );
        return defaultFilters;
    }
    const [filters, setFilters] = useState(getDefaultFilters());


    const getViewData = async () => {
        setAssessmentCacheTime(new Date().getTime());
        let items: FatigueAssessmentForm[] = [];

        if (currentView === FatigueAssessmentView.MyForm) {
            items = await getFatigueAssessments();
            setAdminView(false);
            setAssessmentCacheTime(new Date().getTime());
            return items;
        }

        setAdminView(true);
        let dashboardQuery = { pending: false } as DashboardQueryModel;

        let supervisorFilters = filters.filter(f => f.type === FilterType.supervisor);
        if (supervisorFilters !== undefined && supervisorFilters.length > 0) {
            dashboardQuery.supervisorIds = [];
            supervisorFilters.map((filter) => {
                let value = filter.value;
                if (typeof (value) === 'string') {
                    value = parseInt(value);
                }
                dashboardQuery.supervisorIds?.push(value);
            });
        }

        let locationFilters = filters.filter(f => f.type === FilterType.location);
        if (locationFilters !== undefined && locationFilters.length > 0) {
            dashboardQuery.locationIds = [];
            locationFilters.map((filter) => {
                let value = filter.value;
                if (typeof (value) === 'string') {
                    value = parseInt(value);
                }
                dashboardQuery.locationIds?.push(value);
            });
        }

        let dateFilter = filters.find(f => f.type === FilterType.date);
        if (dateFilter !== undefined) {
            dashboardQuery.date = dateFilter.value;
        }

        if (currentView === FatigueAssessmentView.Pending) {
            dashboardQuery.pending = true;
            items = await getDashboardFatigueAssessments(dashboardQuery);
        }
        else if (currentView === FatigueAssessmentView.ToAction) {
            dashboardQuery.task = FatigueAssessmentTaskEnum.ToAction;
            items = await getDashboardFatigueAssessments(dashboardQuery);
        }
        else if (currentView === FatigueAssessmentView.ToRevisit) {
            dashboardQuery.task = FatigueAssessmentTaskEnum.ToRevisit;
            items = await getDashboardFatigueAssessments(dashboardQuery);
        }
        else if (currentView === FatigueAssessmentView.Completed) {
            dashboardQuery.task = FatigueAssessmentTaskEnum.NoAction;
            items = await getDashboardFatigueAssessments(dashboardQuery);
        }
        else if (currentView === FatigueAssessmentView.Overview) {
            dashboardQuery.overview = true;
            items = await getDashboardFatigueAssessments(dashboardQuery);
        }


        setAssessmentCacheTime(new Date().getTime());
        return items;
    }

    const orderByName = () => {
        let data: FatigueAssessmentForm[] = assessments ? [...assessments] : [];

        data.sort((a, b) => {
            let aName = a.user?.fullname || "";
            let bName = b.user?.fullname || "";
            return aName.localeCompare(bName);
        }
        );

        let sortBy = OrderByOption.NameAsc;
        if (orderByOption == OrderByOption.NameAsc) {
            sortBy = OrderByOption.NameDesc;
            data.reverse();
        }
        setOrderByOption(sortBy);
        setViewAssessments(data);
    }

    const orderByShiftStartTime = () => {
        let data: FatigueAssessmentForm[] = assessments ? [...assessments] : [];

        data.sort((a, b) => {
            let aTime = a.shiftStartTime || Date.now;
            let bTime = b.shiftStartTime || Date.now;
            if (aTime < bTime) return -1;
            if (aTime > bTime) return 1;
            return 0;
        }
        );

        let sortBy = OrderByOption.ShiftStartTimeAsc;
        if (orderByOption == OrderByOption.ShiftStartTimeAsc) {
            sortBy = OrderByOption.ShiftStartTimeDesc;
            data.reverse();
        }

        setOrderByOption(sortBy);
        setViewAssessments(data);
    }

    const configHeaderLink = () => {
        if (!props.showHeaderLink) {
            return;
        }

        let link = undefined as string | string[] | undefined

        if (currentView === FatigueAssessmentView.MyForm) {
            link = fatigueAssessmentRoute.path;
        }
        else if (currentView === FatigueAssessmentView.Pending) {
            link = fatigueAssessmentPendingPathRoute.path;
        }
        else if (currentView === FatigueAssessmentView.ToAction) {
            link = fatigueAssessmentActionPathRoute.path;
        }
        else if (currentView === FatigueAssessmentView.ToRevisit) {
            link = fatigueAssessmentRevisitPathRoute.path;
        }
        else if (currentView === FatigueAssessmentView.Completed) {
            link = fatigueAssessmentCompletedPathRoute.path;
        }

        if (typeof (link) === 'string') {
            setHeaderLink(link);
        }

    }

    const moveToPage = (page: number) => {
        setPageNumber(page);
    }

    const updateCurrentView = (view: FatigueAssessmentView) => {
        setAssessments(undefined);
        setCurrentView(view);
    }

    const handleFilterChange = (filter: FilterItem) => {
        if (filters.some(f => f.type === filter.type && f.value === filter.value)) {
            return; //prevent duplicates
        }

        let newFilters = [...filters];

        // Maximum of 1 date filter, replace old value if found
        if (filter.type === FilterType.date) {
            let oldDateFilterIndex = filters.findIndex(f => { return f.type === FilterType.date });
            if (oldDateFilterIndex > -1) {
                newFilters.splice(oldDateFilterIndex, 1);
            }
        }

        setFilters([...newFilters, filter]);
        setLoadBackground(true);
    }

    const onDeleteFilter = (index: number) => {
        let newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
        setLoadBackground(true);
    }

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            setLoading(true);
            const items = await getViewData();
            configHeaderLink();

            if (mounted && Array.isArray(items)) {
                setAssessments(items);
                setViewAssessments(items);
                setLoading(false);
            }
        };

        if (!assessments) {
            mount();
        }

        return () => { mounted = false; };
    }, [currentView, viewAssessments]);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const items = await getViewData();
            if (mounted && Array.isArray(items)) {
                setAssessments(items);
                setViewAssessments(items);
            }
            setLoadBackground(false);
        }

        if (loadBackground) {
            mount();
        }

        return () => { mounted = false; };
    }, [loadBackground]);

    useEffect(() => {
        const pollingSeconds = 60;
        const cacheAgeThreshold = 50;
        const interval = setInterval(() => {
            if (currentView !== FatigueAssessmentView.MyForm && assessmentCacheTime < new Date().getTime() - (cacheAgeThreshold * 1000)) {
                setLoadBackground(true);
            }
        }, pollingSeconds * 1000);
        return () => {
            clearInterval(interval);
        }
    }, []);

    return (
        <>
            <TitleContainer
                titleIcon={props.titleIcon}
                title={props.title} />

            <ResultCountContainer
                viewCount={viewAssessments.length}
                resultCount={viewAssessments.length}
                pageSize={pageSize}
                setPageSize={setPageSize}
                link={headerLink}
                visible={props.showPageSize} />

            <TabsContainer
                tabs={props.view}
                currentView={currentView}
                setCurrentView={updateCurrentView}
                visible={props.view.length > 1}
            />

            <FilterContainer
                filters={filters}
                showSupervisor={currentView === FatigueAssessmentView.Overview || currentView === FatigueAssessmentView.ToAction}
                onAnswerUpdate={handleFilterChange}
                visible={props.showFilters}
                onDelete={onDeleteFilter}
            />

            <ChartContainer
                assessments={viewAssessments}
                currentView={currentView}
            />

            <FaListContainer
                loading={loading}
                assessments={viewAssessments}
                adminView={adminView}
                currentView={currentView}
                pageNumber={pageNumber}
                pageSize={pageSize}
                orderByOption={orderByOption}
                orderByName={orderByName}
                orderByShiftStartTime={orderByShiftStartTime}
            />

            <FooterContainer
                visible={props.showFooter}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalResults={viewAssessments.length}
                moveToPage={moveToPage}
            />

        </>
    );
}

export default FatigueAssessmentCardList;
